import PropTypes from 'prop-types';
import React from 'react';

import DataConnector from 'connect/DataConnector';
import * as BioentityActions from 'inventory/BioentityActions';
import InventoryFileStore from 'inventory/InventoryFileStore';
import * as InventoryFileUtil from 'inventory/InventoryFileUtil';
import {ButtonStyle} from 'ui-library/buttons/Button';
import Col from 'ui-library/bs/Col';
import Modal from 'ui-library/modals/Modal';
import Row from 'ui-library/bs/Row';
import * as ModalContainer from 'ui-library/ModalContainer';
import EntityStore from 'stores/EntityStore';

function mapToInPersonalCollections(allBioentityAliasInfos) {
  const personalCollections = [];
  allBioentityAliasInfos.forEach((bioentityAliasInfo) => {
    personalCollections.push(InventoryFileUtil.hasPersonalCollection(bioentityAliasInfo));
  });
  return personalCollections;
}

function mapToInOrgCollections(allBioentityAliasInfos) {
  const orgCollections = [];
  allBioentityAliasInfos.forEach((aliases) => {
    orgCollections.push(InventoryFileUtil.hasOrgCollection(aliases));
  });
  return orgCollections;
}

function areAllFilesInPersonalCollections(allBioentityAliasInfos) {
  return mapToInPersonalCollections(allBioentityAliasInfos).every(
    (isInPersonalCollection) => isInPersonalCollection
  );
}

function areAllFilesInOrgCollections(allBioentityAliasInfos) {
  return mapToInOrgCollections(allBioentityAliasInfos).every((isInOrgCollection) => isInOrgCollection);
}

function arePersonalCollectionsEqual(allBioentityAliasInfos) {
  const personalCollections = mapToInPersonalCollections(allBioentityAliasInfos);
  return personalCollections.every(
    (isInPersonalCollection) => isInPersonalCollection === personalCollections[0]
  );
}

function areOrgCollectionsEqual(allBioentityAliasInfos) {
  const orgCollections = mapToInOrgCollections(allBioentityAliasInfos);
  return orgCollections.every((isInOrgCollection) => isInOrgCollection === orgCollections[0]);
}

export function openSetCollectionModal(props) {
  const onRequestClose = () => ModalContainer.close(SetCollectionModal);
  ModalContainer.open(
    SetCollectionModal,
    <ConnectedSetCollectionModal
      {...props}
      currentUser={EntityStore.currentUser()}
      onRequestClose={onRequestClose}
    />
  );
}

class SetCollectionModalConnector extends DataConnector {
  shouldSubscribeToRedux = false;
  stores = [InventoryFileStore];

  enhanceProps() {
    const bioentityAliasInfos = new Map();
    this.props.files.forEach(({id: fileId}) => {
      const aliases = this.inventoryFileStore().getBioentityAliasInfos(fileId);
      if (aliases) {
        bioentityAliasInfos.set(fileId, aliases);
      }
    });
    if (bioentityAliasInfos.size !== this.props.files.length) {
      return {bioentityAliasInfos: null};
    }
    return {bioentityAliasInfos};
  }
}

class SetCollectionModal extends React.Component {
  static propTypes = {
    onRequestClose: PropTypes.func,
    files: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
      })
    ).isRequired,
    // Provided by SetCollectionModalConnector
    bioentityAliasInfos: PropTypes.instanceOf(Map),
  };

  componentDidMount() {
    if (!this.props.bioentityAliasInfos) {
      BioentityActions.loadBioentityAliasInfos(this.props.files.map(({id}) => id));
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.bioentityAliasInfos !== this.props.bioentityAliasInfos) {
      this.setState(this._getStateFromAliases());
    }
  }

  _getStateFromAliases = () => ({
    isPersonalCollection: areAllFilesInPersonalCollections(this.props.bioentityAliasInfos),
    isOrgCollection: areAllFilesInOrgCollections(this.props.bioentityAliasInfos),
  });

  _hasCollection = () => this.state.isPersonalCollection || this.state.isOrgCollection;

  _enableSubmit = () => {
    if (!this.props.bioentityAliasInfos || !this._hasCollection() || !this._allCollectionsMatch()) {
      return false;
    }
    return true;
  };

  _allCollectionsMatch = () =>
    arePersonalCollectionsEqual(this.props.bioentityAliasInfos) &&
    areOrgCollectionsEqual(this.props.bioentityAliasInfos);

  _renderFilesWarning = () => {
    if (this.props.files.length === 0 || !this.props.bioentityAliasInfos) {
      return null;
    }

    if (this._allCollectionsMatch()) {
      return null;
    }

    return (
      <span className="u-danger-color u-tiny-spacing-left">
        Files must be in the same collections in order to bulk add or remove.
      </span>
    );
  };

  _renderCollectionWarning = () => {
    if (this._hasCollection()) {
      return null;
    }
    return <span className="u-danger-color u-tiny-spacing-left">Please select at least one collection</span>;
  };

  _setCollections = () => {
    const fileIds = this.props.files.map(({id}) => id);
    BioentityActions.updateRegistryCollections(fileIds, {
      inOrgCollection: this.state.isOrgCollection,
      inPersonalCollection: this.state.isPersonalCollection,
    });
    this.props.onRequestClose();
  };

  _renderModalContents = () => (
    <div>
      <div>
        <label>Files</label>
        {this._renderFilesWarning()}
        <div>{this.props.files.map(({name}) => name).join(', ')}</div>
      </div>
      <div className="u-spacing-top">
        <label>Collection</label>
        {this._renderCollectionWarning()}
        <Row className="u-tiny-spacing-top">
          <Col sm={6}>
            <label className="u-normal-font-weight">
              <input
                autoFocus={true}
                checked={this.state.isPersonalCollection}
                onChange={(e) => this.setState({isPersonalCollection: e.target.checked})}
                type="checkbox"
              />
              Personal Collection
            </label>
          </Col>
          <Col sm={6}>
            <label className="u-normal-font-weight">
              <input
                type="checkbox"
                checked={this.state.isOrgCollection}
                onChange={(e) => this.setState({isOrgCollection: e.target.checked})}
              />
              Organization Collection
            </label>
          </Col>
        </Row>
      </div>
    </div>
  );

  state = {
    isPersonalCollection: true,
    isOrgCollection: true,
    // eslint-disable-next-line react/no-unused-state
    currentUser: EntityStore.currentUser(),
    ...this._getStateFromAliases(),
  };

  render() {
    return (
      <Modal
        title="Change collection"
        onRequestClose={this.props.onRequestClose}
        buttons={[
          {
            disabled: !this._enableSubmit(),
            key: 'submit',
            onClick: this._setCollections,
            style: ButtonStyle.PRIMARY,
            submit: true,
            text: 'Save',
          },
        ]}
      >
        {this.props.bioentityAliasInfos ? (
          this._renderModalContents()
        ) : (
          <div className="loading-spinner-background u-small-padding" />
        )}
      </Modal>
    );
  }
}

const ConnectedSetCollectionModal = SetCollectionModalConnector.connect(SetCollectionModal);
export default ConnectedSetCollectionModal;
